import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { useSignIn, useSignUp } from '@clerk/clerk-react';
import BrandLogo from './BrandLogo';

function clerkErrorToMessage(err) {
  const first = err?.errors?.[0];
  return (
    first?.longMessage ||
    first?.message ||
    err?.message ||
    'Something went wrong. Please try again.'
  );
}

const AuthScreen = ({ initialMode = 'signIn', onBack }) => {
  const { signIn, setActive, isLoaded: signInLoaded } = useSignIn();
  const { signUp, setActive: setActiveUp, isLoaded: signUpLoaded } = useSignUp();

  const [mode, setMode] = useState(initialMode); // 'signIn' | 'signUp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  /** Clerk sign-up with email verification sends a code; collect it here. */
  const [signupStep, setSignupStep] = useState('credentials'); // 'credentials' | 'verify_email'
  const [verificationCode, setVerificationCode] = useState('');

  const canSubmit = useMemo(() => email.trim() && password, [email, password]);
  const canVerifyCode = useMemo(() => verificationCode.trim().length >= 4, [verificationCode]);

  const startGoogle = useCallback(async () => {
    setError('');
    try {
      // Redirect-based OAuth is the most reliable in SPA auth flows.
      if (mode === 'signUp') {
        if (!signUpLoaded) return;
        await signUp.authenticateWithRedirect({
          strategy: 'oauth_google',
          redirectUrl: window.location.href,
          redirectUrlComplete: window.location.href,
        });
      } else {
        if (!signInLoaded) return;
        await signIn.authenticateWithRedirect({
          strategy: 'oauth_google',
          redirectUrl: window.location.href,
          redirectUrlComplete: window.location.href,
        });
      }
    } catch (e) {
      setError(clerkErrorToMessage(e));
    }
  }, [mode, signIn, signInLoaded, signUp, signUpLoaded]);

  const submit = useCallback(async () => {
    setError('');
    if (!canSubmit) return;
    setLoading(true);
    try {
      if (mode === 'signIn') {
        if (!signInLoaded) return;
        const res = await signIn.create({
          identifier: email.trim(),
          password,
        });
        if (res.status !== 'complete') {
          throw new Error('Additional verification required. Please use Google sign-in or enable password sign-in in Clerk.');
        }
        await setActive({ session: res.createdSessionId });
        return;
      }

      // Sign up
      if (!signUpLoaded) return;
      const res = await signUp.create({
        emailAddress: email.trim(),
        password,
      });

      if (res.status === 'complete' && res.createdSessionId) {
        await setActiveUp({ session: res.createdSessionId });
        return;
      }

      if (res.status === 'missing_requirements') {
        try {
          await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
          setSignupStep('verify_email');
          setVerificationCode('');
          return;
        } catch (prepErr) {
          throw new Error(
            clerkErrorToMessage(prepErr) ||
              'Could not send a verification email. Check Clerk email settings or try Google sign-in.',
          );
        }
      }

      throw new Error(
        'Sign up needs an extra step in Clerk (e.g. phone or legal). Try Google sign-in or adjust your Clerk sign-up settings.',
      );
    } catch (e) {
      setError(clerkErrorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, [canSubmit, email, mode, password, setActive, setActiveUp, signIn, signInLoaded, signUp, signUpLoaded]);

  const submitVerificationCode = useCallback(async () => {
    setError('');
    if (!canVerifyCode || !signUpLoaded) return;
    setLoading(true);
    try {
      const res = await signUp.attemptEmailAddressVerification({
        code: verificationCode.trim(),
      });
      if (res.status === 'complete' && res.createdSessionId) {
        await setActiveUp({ session: res.createdSessionId });
        setSignupStep('credentials');
        return;
      }
      setError('That code did not work. Check the email or request a new code.');
    } catch (e) {
      setError(clerkErrorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, [canVerifyCode, setActiveUp, signUp, signUpLoaded, verificationCode]);

  const resendVerificationCode = useCallback(async () => {
    setError('');
    if (!signUpLoaded) return;
    setLoading(true);
    try {
      await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
    } catch (e) {
      setError(clerkErrorToMessage(e));
    } finally {
      setLoading(false);
    }
  }, [signUp, signUpLoaded]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 6,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          width: '100%',
          maxWidth: 560,
          borderRadius: 4,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, sm: 3.25 },
            py: 2.75,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            background:
              'linear-gradient(90deg, rgba(168, 85, 247, 0.16) 0%, rgba(99, 102, 241, 0.10) 45%, rgba(34, 211, 238, 0.08) 100%)',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <BrandLogo size={36} showText={false} onClick={onBack} />
            {onBack ? (
              <Button variant="outlined" onClick={onBack} sx={{ borderRadius: 999, px: 2 }}>
                Back
              </Button>
            ) : null}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 900, mt: 1.25, letterSpacing: 0.2 }}>
            {mode === 'signUp' ? 'Create your account' : 'Sign in'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {mode === 'signUp'
              ? 'Use email/password or continue with Google.'
              : 'Welcome back. Use email/password or continue with Google.'}
          </Typography>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, sm: 3.25 } }}>
          <Tabs
            value={mode}
            onChange={(_, v) => {
              setMode(v);
              setError('');
              setSignupStep('credentials');
              setVerificationCode('');
            }}
            sx={{
              mb: 2.5,
              '& .MuiTabs-indicator': { backgroundColor: 'rgba(168, 85, 247, 0.9)' },
            }}
          >
            <Tab value="signIn" label="Sign in" />
            <Tab value="signUp" label="Sign up" />
          </Tabs>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {mode === 'signUp' && signupStep === 'verify_email' ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Enter the verification code we sent to <strong>{email.trim()}</strong>.
                </Typography>
                <TextField
                  label="Verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  autoComplete="one-time-code"
                  inputProps={{ inputMode: 'numeric', autoCapitalize: 'off', spellCheck: false }}
                  fullWidth
                  disabled={loading}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={submitVerificationCode}
                  disabled={loading || !canVerifyCode || !signUpLoaded}
                  sx={{ borderRadius: 2.5, py: 1.15 }}
                >
                  {loading ? 'Please wait…' : 'Verify email'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={resendVerificationCode}
                  disabled={loading || !signUpLoaded}
                  sx={{ color: 'rgba(168, 85, 247, 0.95)' }}
                >
                  Resend code
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSignupStep('credentials');
                    setVerificationCode('');
                    setError('');
                  }}
                  disabled={loading}
                  sx={{ borderRadius: 2.5 }}
                >
                  Use a different email
                </Button>
              </>
            ) : (
              <>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={startGoogle}
                  disabled={loading || !(signInLoaded && signUpLoaded)}
                  sx={{
                    borderRadius: 2.5,
                    py: 1.1,
                    borderColor: 'rgba(255,255,255,0.10)',
                    backgroundColor: 'rgba(255,255,255,0.03)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' },
                  }}
                >
                  Continue with Google
                </Button>

                <Divider sx={{ my: 0.5, opacity: 0.25 }}>or</Divider>

                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@company.com"
                  fullWidth
                  disabled={loading}
                />
                <TextField
                  label="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type={showPw ? 'text' : 'password'}
                  autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                  fullWidth
                  disabled={loading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPw((p) => !p)}
                          edge="end"
                          size="small"
                          aria-label={showPw ? 'Hide password' : 'Show password'}
                        >
                          {showPw ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={submit}
                  disabled={loading || !canSubmit || !(signInLoaded && signUpLoaded)}
                  sx={{ borderRadius: 2.5, py: 1.15 }}
                >
                  {loading ? 'Please wait…' : mode === 'signUp' ? 'Create account' : 'Continue'}
                </Button>
              </>
            )}

            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', mt: 0.5 }}>
              Secured by Clerk.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AuthScreen;

